import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsAPI } from '../../utils/api';
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useSocketStore } from '../../stores/socketStore';
import clsx from 'clsx';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const queryClient = useQueryClient();
  const onNewAlert = useSocketStore((state) => state.onNewAlert);

  const { data, refetch } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () => notificationsAPI.getHistory({ page: 1, limit: 5 }),
    refetchInterval: 30000,
  });

  // Subscribe to real-time alert notifications
  useEffect(() => {
    // Register callback to refetch notifications when new alert arrives
    const unsubscribe = onNewAlert(() => {
      refetch();
    });

    return unsubscribe;
  }, [onNewAlert, refetch]);

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId) => notificationsAPI.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate and refetch the notifications query
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
    },
  });

  const raw = data?.data?.data || data?.data || [];
  const notifications = Array.isArray(raw) ? raw : [];
  const unreadCount = notifications.filter(n => !n.readAt).length;

  const handleNotificationClick = (notification) => {
    if (!notification.readAt && !notification.isAlert) {
      markAsReadMutation.mutate(notification.id);
    }
  };

  // Close on outside click or Escape
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const keyHandler = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, []);

  const formatTime = (date) => {
    if (!date) return '';
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const severityColor = {
    CRITICAL: 'bg-red-500',
    HIGH: 'bg-orange-500',
    MEDIUM: 'bg-amber-500',
    LOW: 'bg-blue-500',
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="icon-btn relative"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <BellIcon className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden animate-fade-in" role="menu" aria-label="Notifications">
          <div className="px-4 py-3 border-b border-slate-100 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 text-sm">Notifications</h3>
              <Link
                to="/alerts"
                onClick={() => setOpen(false)}
                className="text-xs font-medium"
                style={{ color: 'var(--theme-600)' }}
              >
                View All
              </Link>
            </div>
            {unreadCount > 0 && (
              <p className="text-xs text-slate-600 font-medium">
                💡 Hover over unread notifications and click the ✓ icon to mark as read
              </p>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <BellIcon className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={clsx(
                    'w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex items-start gap-3 group',
                    !notification.readAt && 'bg-blue-50/50'
                  )}
                >
                  <div className={clsx(
                    'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                    severityColor[notification.severity] || 'bg-slate-400'
                  )} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800 truncate flex-1">
                        {notification.title || notification.type?.replace(/_/g, ' ')}
                      </p>
                      {!notification.readAt && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-slate-600 truncate">
                      {notification.message || notification.body}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatTime(notification.createdAt)}
                    </p>
                  </div>
                  {!notification.readAt && !notification.isAlert && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNotificationClick(notification);
                      }}
                      className="p-2 rounded-lg text-blue-500 hover:bg-blue-100 hover:text-blue-600 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                      title="Mark as read"
                    >
                      <CheckIcon className="w-5 h-5" />
                    </button>
                  )}
                  {notification.readAt && (
                    <div className="p-2 flex-shrink-0">
                      <CheckIcon className="w-5 h-5 text-emerald-500" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
